import './new-type-ahead.scss';

import { Controller, useFormContext } from 'react-hook-form';
import { Box, FormHelperText, SelectChangeEvent } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';

import Autocomplete from '@mui/material/Autocomplete';
import { FormFieldContainer } from './form-field-container';
import { NewConvergeInputLabel } from './new-converge-input-label';
import TextField from '@mui/material/TextField';

export type TypeHeadValue = string | number | null;

export interface ListOptions<D, V extends TypeHeadValue = string> {
  value: V;
  label: string;
  data: D;
}

interface NewTypeHeadProps<D = unknown, V extends TypeHeadValue = string> {
  label?: string | null;
  className?: string;
  placeholder?: string;
  required?: boolean;
  value?: V | null;
  name: string;
  fieldName?: string;
  containerWidth?: number;
  options?: D[]; // Make options optional and handle undefined case
  onChange?: (event: SelectChangeEvent<NonNullable<V>>) => void;
  mapToOption?: (
    item: D
  ) => Omit<ListOptions<D, V>, 'data'> &
    Partial<Pick<ListOptions<D, V>, 'data'>>;
  isLoading?: boolean;
  onChangeOption?: (item: ListOptions<D, V>) => void;
  hideLabel?: boolean;
  hideBorders?: boolean; // New prop to control border visibility
  debug?: boolean;
  disabled?: boolean;
  dataTestId?: string;
  minWidthDropdown?: string;
  tooltip?: React.ReactNode;
  maxHeightDropdown?: number;
  dropdownOptionWidth?: boolean;
  description?: string;
  clearable?: boolean; // New prop to enable clear functionality
}

export const NewTypeAhead = <D, V extends TypeHeadValue = string>({
  name,
  label = name,
  fieldName,
  required,
  containerWidth = 6,
  options = [], // Default to empty array if options is not provided
  mapToOption,
  isLoading,
  placeholder,
  disabled,
  onChangeOption,
  hideLabel = false,
  hideBorders = false, // Default to showing borders
  value: valueProp,
  debug,
  dataTestId,
  tooltip,
  minWidthDropdown = '250px',
  dropdownOptionWidth,
  maxHeightDropdown,
  description,
  clearable = true, // Default to clearable
  ...props
}: NewTypeHeadProps<D, V>) => {
  const [inputValue, setInputValue] = useState<string>('');
  const {
    control,
    formState: { errors },
    watch,
    setValue,
  } = useFormContext();

  const listOptions = useMemo((): ListOptions<D, V>[] => {
    if (!Array.isArray(options) || !options.length) {
      return [];
    }

    if (mapToOption) {
      return options.map((item) => {
        const option = mapToOption(item);
        option.data = item;
        return option as ListOptions<D, V>;
      });
    }

    // If mapToOption is not provided, options should already be in the correct format
    return options as ListOptions<D, V>[];
  }, [mapToOption, options]);

  const error = errors[name];

  const currentValue = valueProp ?? watch(name);

  const selectedListOption = listOptions?.find(
    (option) => option?.value === currentValue
  );

  useEffect(() => {
    if (!selectedListOption) {
      return;
    }
    if (selectedListOption.value !== currentValue) {
      setValue(name, selectedListOption.value, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
    }
  }, [setValue, name, selectedListOption, currentValue]);

  return (
    <FormFieldContainer md={containerWidth} sx={{ height: '10px' }}>
      {hideLabel === false && (
        <NewConvergeInputLabel
          className={error?.message ? 'error-message' : ''}
          label={label}
          required={required}
          tooltip={tooltip}
        />
      )}
      {description && (
        <Box
          style={{
            marginBottom: '4px',
            fontStyle: 'italic',
            fontSize: '14px',
            color: 'black !important;',
          }}
        >
          {description}
        </Box>
      )}
      <Controller
        control={control}
        name={name}
        render={({ field: { ref, onChange, value, ...otherField } }) => (
          <Autocomplete
            disabled={disabled}
            className="new-type-ahead"
            disablePortal
            disableClearable={!clearable}
            loading={isLoading}
            value={selectedListOption ?? null}
            fullWidth
            onChange={(event, newValue) => {
              onChange(newValue ? newValue.value : null);
              // Always call onChangeOption, even when newValue is null
              if (onChangeOption) {
                onChangeOption(newValue as ListOptions<D, V>);
              }
            }}
            inputValue={inputValue}
            onInputChange={(event, newInputValue) => {
              setInputValue(newInputValue);
            }}
            options={listOptions}
            getOptionLabel={(option) => option.label}
            componentsProps={{
              popper: {
                sx: dropdownOptionWidth
                  ? {}
                  : {
                      width: 'auto !important',
                      minWidth: `${minWidthDropdown} !important`,
                    },
              },
            }}
            ListboxProps={{
              style: {
                maxHeight: maxHeightDropdown ?? '40vh',
                paddingTop: 0,
                paddingBottom: 0,
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={placeholder}
                disabled={disabled}
                error={!!error}
                inputProps={{
                  ...params.inputProps,
                  ...(dataTestId && { 'data-testid': dataTestId }),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: ' 14px !important',
                    fontWeight: '600 !important',
                    background: 'white',
                    height: '37px',
                    ...(error && {
                      '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#d32f2f !important',
                      },
                      '&.Mui-error:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#d32f2f !important',
                      },
                      '&.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline':
                        {
                          borderColor: '#d32f2f !important',
                          borderWidth: '2px !important',
                        },
                      // Additional high specificity selectors to override MUI generated classes
                      '&.MuiInputBase-root.Mui-error .MuiOutlinedInput-notchedOutline':
                        {
                          borderColor: '#d32f2f !important',
                        },
                      '&.MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline':
                        {
                          borderColor: '#d32f2f !important',
                        },
                      '&.MuiInputBase-root.MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline':
                        {
                          borderColor: '#d32f2f !important',
                        },
                      '&.MuiInputBase-colorPrimary.Mui-error .MuiOutlinedInput-notchedOutline':
                        {
                          borderColor: '#d32f2f !important',
                        },
                      '&.MuiAutocomplete-inputRoot.Mui-error .MuiOutlinedInput-notchedOutline':
                        {
                          borderColor: '#d32f2f !important',
                        },
                    }),
                    '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
                      borderColor: hideBorders
                        ? 'transparent !important'
                        : 'rgba(0, 0, 0, 0.22) !important', // Transparent or disabled gray border based on hideBorders
                    },
                    '&.Mui-disabled:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: hideBorders
                        ? 'transparent !important'
                        : 'rgba(0, 0, 0, 0.22) !important', // Force transparent or same gray color on hover
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: hideBorders ? 'black' : '#28c0a7', // $Green-dark
                      borderWidth: hideBorders ? '0' : '3px',
                      border: hideBorders
                        ? 'none'
                        : '2px solid #28c0a7 !important',
                    },
                    '&:hover:not(.Mui-focused):not(.Mui-disabled) .MuiOutlinedInput-notchedOutline':
                      {
                        borderColor: hideBorders ? 'transparent' : '#28c0a7', // $Green-Darker
                      },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: hideBorders ? 'transparent' : 'black',
                    },
                  },
                  '& .MuiInputBase-root': {
                    display: 'flex',
                    '& .MuiInputBase-input': {
                      textOverflow: 'ellipsis !important',
                      overflow: 'hidden !important',
                      whiteSpace: 'nowrap !important',
                      width: '100% !important',
                    },
                  },
                  '& .MuiAutocomplete-endAdornment': {
                    position: 'absolute',
                    right: '9px',
                  },
                  '& .MuiAutocomplete-popupIndicator .MuiSvgIcon-root': {
                    color: hideBorders
                      ? 'transparent'
                      : disabled
                      ? 'rgba(0, 0, 0, 0.22)'
                      : 'black',
                    visibility: hideBorders ? 'hidden' : 'visible',
                  },
                  '& .MuiAutocomplete-clearIndicator': {
                    visibility: hideBorders ? 'hidden' : 'hidden',
                    opacity: hideBorders ? 0 : 0,
                    transition: 'opacity 0.2s, visibility 0.2s',
                  },
                  '& .MuiAutocomplete-clearIndicator .MuiSvgIcon-root': {
                    color: hideBorders
                      ? 'transparent'
                      : disabled
                      ? 'rgba(0, 0, 0, 0.22)'
                      : 'black',
                    visibility: hideBorders ? 'hidden' : 'hidden',
                    opacity: hideBorders ? 0 : 0,
                    transition: 'opacity 0.2s, visibility 0.2s',
                  },
                  // Show clear icon on hover or focus
                  '&:hover .MuiAutocomplete-clearIndicator': {
                    visibility: hideBorders ? 'hidden' : 'visible',
                    opacity: hideBorders ? 0 : 1,
                  },
                  '&:hover .MuiAutocomplete-clearIndicator .MuiSvgIcon-root': {
                    visibility: hideBorders ? 'hidden' : 'visible',
                    opacity: hideBorders ? 0 : 1,
                  },
                  '&.Mui-focused .MuiAutocomplete-clearIndicator': {
                    visibility: hideBorders ? 'hidden' : 'visible',
                    opacity: hideBorders ? 0 : 1,
                  },
                  '&.Mui-focused .MuiAutocomplete-clearIndicator .MuiSvgIcon-root':
                    {
                      visibility: hideBorders ? 'hidden' : 'visible',
                      opacity: hideBorders ? 0 : 1,
                    },
                  // Also target when input is focused
                  '& .MuiOutlinedInput-root.Mui-focused .MuiAutocomplete-clearIndicator':
                    {
                      visibility: hideBorders ? 'hidden' : 'visible',
                      opacity: hideBorders ? 0 : 1,
                    },
                  '& .MuiOutlinedInput-root.Mui-focused .MuiAutocomplete-clearIndicator .MuiSvgIcon-root':
                    {
                      visibility: hideBorders ? 'hidden' : 'visible',
                      opacity: hideBorders ? 0 : 1,
                    },
                  '& .MuiOutlinedInput-root:hover .MuiAutocomplete-clearIndicator':
                    {
                      visibility: hideBorders ? 'hidden' : 'visible',
                      opacity: hideBorders ? 0 : 1,
                    },
                  '& .MuiOutlinedInput-root:hover .MuiAutocomplete-clearIndicator .MuiSvgIcon-root':
                    {
                      visibility: hideBorders ? 'hidden' : 'visible',
                      opacity: hideBorders ? 0 : 1,
                    },
                  '& input::placeholder': {
                    opacity: 1,
                    fontWeight: 600,
                    color: 'rgba(0, 0, 0, 0.6)',
                  },
                  '&.MuiPopper-root': {
                    position: ' absolute !important',
                    width: 'auto !important',
                    minWidth: ` ${minWidthDropdown}!important`,
                  },
                }}
              />
            )}
            {...otherField}
          />
        )}
      />
      {error && (
        <FormHelperText error className="form-select-form-helper-text">
          {(error.message as string) || 'This field is required'}
        </FormHelperText>
      )}
    </FormFieldContainer>
  );
};
